
// // src/hooks/useVisitorRequests.js
// import { useEffect, useState, useCallback } from 'react';
// import {
//     collection, query, where, onSnapshot, orderBy,
//     addDoc, doc, updateDoc, serverTimestamp, getDoc
// } from 'firebase/firestore';
// import { db } from '../firebase';

// export function useVisitorRequests({ communityId, residentUid, statusIn, limitTo = 50 }) {
//     const [rows, setRows] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [err, setErr] = useState('');

//     useEffect(() => {
//         if (!communityId && !residentUid) {
//             setRows([]); setLoading(false); return;
//         }
//         setLoading(true); setErr('');

//         const parts = [collection(db, 'visitorRequests')];
//         let q = query(...parts);

//         if (communityId) q = query(q, where('communityId', '==', communityId));
//         if (residentUid) q = query(q, where('residentUid', '==', residentUid));
//         if (Array.isArray(statusIn) && statusIn.length)
//             q = query(q, where('status', 'in', statusIn));

//         q = query(q, orderBy('createdAt', 'desc'));

//         const unsub = onSnapshot(q, (snap) => {
//             setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
//             setLoading(false);
//         }, (e) => {
//             console.error('useVisitorRequests error', e);
//             setErr(e?.message || 'Failed to load requests');
//             setLoading(false);
//         });

//         return () => unsub();
//     }, [communityId, residentUid, JSON.stringify(statusIn), limitTo]);

//     return { rows, loading, err };
// }

// // write actions
// export function useVisitorActions() {
//     const createRequest = useCallback(async (payload) => {
//         const now = serverTimestamp();
//         const docRef = await addDoc(collection(db, 'visitorRequests'), {
//             ...payload,
//             createdAt: now,
//             updatedAt: now,
//         });
//         return docRef.id;
//     }, []);

//     const updateRequest = useCallback(async (id, patch) => {
//         const ref = doc(db, 'visitorRequests', id);
//         await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
//     }, []);

//     const getById = useCallback(async (id) => {
//         const ref = doc(db, 'visitorRequests', id);
//         const snap = await getDoc(ref);
//         if (!snap.exists()) return null;
//         return { id: snap.id, ...snap.data() };
//     }, []);

//     return { createRequest, updateRequest, getById };
// }


// src/hooks/useVisitorRequests.js
import { useEffect, useState, useCallback } from 'react';
import {
    collection, query, where, onSnapshot, orderBy,
    addDoc, doc, updateDoc, serverTimestamp, getDoc, limit, getDocs
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * QuickStart mode (no index needed):
 * - Pass quickStart=true to avoid filtering by status/orderBy (less efficient but no composite index)
 * Recommended mode:
 * - Provide { communityId, statusIn, orderByCreatedDesc } => will need composite index
 */
export function useVisitorRequests({ communityId, statusIn, orderByCreatedDesc = true, quickStart = false }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (!communityId) { setRows([]); setLoading(false); return; }
        setLoading(true); setErr('');

        let q = query(collection(db, 'visitorRequests'));

        if (quickStart) {
            // Minimal filter to avoid index for first run:
            // just filter by communityId (no status IN, no orderBy)
            q = query(q, where('communityId', '==', communityId));
        } else {
            // Recommended (needs composite index: communityId ASC, status ASC, createdAt DESC)
            q = query(q, where('communityId', '==', communityId));
            if (Array.isArray(statusIn) && statusIn.length) {
                q = query(q, where('status', 'in', statusIn));
            }
            if (orderByCreatedDesc) {
                q = query(q, orderBy('createdAt', 'desc'));
            }
        }

        const unsub = onSnapshot(q, (snap) => {
            setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, (e) => {
            console.error('useVisitorRequests error', e);
            setErr(e?.message || 'Failed to load requests');
            setLoading(false);
        });

        return () => unsub();
    }, [communityId, JSON.stringify(statusIn), orderByCreatedDesc, quickStart]);

    return { rows, loading, err };
}

export function useVisitorActions() {
    const createRequest = useCallback(async (payload) => {
        const now = serverTimestamp();
        const ref = await addDoc(collection(db, 'visitorRequests'), {
            ...payload,
            createdAt: now,
            updatedAt: now,
        });
        return ref.id;
    }, []);

    const updateRequest = useCallback(async (id, patch) => {
        await updateDoc(doc(db, 'visitorRequests', id), { ...patch, updatedAt: serverTimestamp() });
    }, []);

    const getById = useCallback(async (id) => {
        const snap = await getDoc(doc(db, 'visitorRequests', id));
        if (!snap.exists()) return null;
        return { id: snap.id, ...snap.data() };
    }, []);

    // Helper for QR scan: lookup by qrToken (no index needed if kept simple)
    const getByQrToken = useCallback(async (token) => {
        const q = query(collection(db, 'visitorRequests'), where('qrToken', '==', token), limit(1));
        const snap = await getDocs(q);
        if (snap.empty) return null;
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
    }, []);

    return { createRequest, updateRequest, getById, getByQrToken };
}
